import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS } from "../../../../../../constants";
import { CopyComponent } from "../../../../../../parts/CopyComponent";
import { SearchDropdown } from "../../../../../../parts/inputs/search-dropdown";
import { getExerciseName } from "../../../../../../parts/modals/learn-more/blockHelpers";
import SvgIcon from "../../../../../../parts/SvgIcon";
import Tooltip from "../../../../../../parts/tooltip-portal";
import {
  anySetCompleted,
  getAllSetsCounts,
} from "../../../../../../utils/actuals";
import { getMovementsData } from "../../../../../../utils/exercise-library";
import { calculatePrefixForExercise } from "../../../../../../utils/prefix-calculator";
import { SuperSetButton } from "./Buttons";
import ExerciseContent from "./Content";
import MenuOptionsEditing from "./MenuOptions";
import Notes from "./Notes";
import WorkoutParamsRow from "./WorkoutParamsRow";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const ExerciseEditor = ({
  exercise,
  exercises,
  keyId,
  activeUser,
  athlete,
  editDayMode,
  supersets,
  handleSelectChange,
  selectedExercise,
  dayString,
  athleteId,
  tempExercises,
  showOptions,
  getTempExercise,
  handleInput,
  addTempSet,
  removeTempSet,
  repMaximums,
  removeSet,
  handleSuperset,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  activeUserToRequest,
  date,
  sectionPrefix,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    if (!editDayMode) setAddingNote(false);
  }, [editDayMode]);

  useEffect(() => {
    addingNote &&
      document.getElementById(`exercise-${exercise._id}-notes`)?.focus();
  }, [addingNote]);

  const prefix =
    sectionPrefix ?? calculatePrefixForExercise(exercise._id, supersets);
  const movements = getMovementsData(exercise, activeUserToRequest, athlete);
  const someHasRPE = exercise.sets.some((set) => set.rpe);
  const exerciseId = exercise._id;

  const editable = !exercise.sets.some((set) => set.outcomes);
  // Completion chip next to the name, so a coach scanning the calendar in edit
  // mode can tell a finished workout from a pending one at a glance.
  const hasActuals = anySetCompleted(exercise);
  const { total: totalSets, completed: completedSets } =
    getAllSetsCounts(exercise);
  const selectExercise =
    tempExercises?.find((ex) => ex._id === exerciseId) || exercise;

  const newExercise = getTempExercise(exerciseId);

  const isLast = keyId === exercises.length - 1;

  const isSuperset =
    !isLast && exercise.nextSuperset === exercises[keyId + 1]._id;

  const getTempSets = (arr, id = exercise._id) => {
    const result = arr.find((ex) => ex._id === id);
    return result ? result.sets : [];
  };

  const complexTwo = exercise.exercises?.[1];
  const complexThree = exercise.exercises?.[2];

  const finalSets =
    editDayMode && editable ? getTempSets(tempExercises) : exercise.sets;
  const complexTwoSets =
    editDayMode && editable
      ? getTempSets(tempExercises, complexTwo?._id || "1")
      : complexTwo?.sets;
  const complexThreeSets =
    editDayMode && editable
      ? getTempSets(tempExercises, complexThree?._id || "2")
      : complexThree?.sets;

  const onAddNote = () => {
    setAddingNote(true);
  };

  const normalizedNotes =
    typeof exercise.notes === "string"
      ? exercise.notes
      : String(exercise.notes || "");

  const isComplex = !!exercise.complex;
  const nextIsComplex = !!exercises[keyId + 1]?.complex;
  const isMetcon = exercise.primary === "metcon";
  const nextIsMetcon = !!exercises[keyId + 1]?.primary === "metcon";

  return (
    <div
      className={`exercisex id_${exerciseId}`}
      key={keyId}
      id={exerciseId}
      data-date={date.format(DATE_DAY_FORMAT)}
    >
      <div
        className={`titlex exercise-element-title${
          editDayMode && editable
            ? " editing"
            : editDayMode
              ? " not-editable"
              : ""
        }`}
      >
        <div className="title-content">
          <span className="calendar-prefix">{prefix}</span>
          <div
            className={`name-order${
              !(
                !editDayMode &&
                  editable &&
                  showOptions &&
                  selectedExercise === exerciseId
              )
                ? " editing"
                : ""
            }`}
          >
            {editDayMode ? (
              editable ? (
                <SearchDropdown
                  options={movements}
                  placeholder={getExerciseName(selectExercise)}
                  valueKey="movementLabel"
                  handleChange={(val) => handleSelectChange(val, exerciseId)}
                  disabled={isComplex || isMetcon}
                />
              ) : (
                <Tooltip body={t("exerciseEditor.exerciseCompletedNoEdit")}>
                  <p>{getExerciseName(exercise)}</p>
                </Tooltip>
              )
            ) : (
              <p>{getExerciseName(exercise)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Sits with the right-hand controls rather than beside the name:
              .name-order takes w-full whenever the movement dropdown is
              suppressed (which is always the case here), leaving no room in
              .title-content. This group is shrink-0 and empty for a completed
              exercise, since the copy/drag/meatball controls are all gated on
              `editable`. */}
          {hasActuals && (
            <span
              className={`wk-ex-status shrink-0 ${
                completedSets >= totalSets ? "ok" : "partial"
              }`}
            >
              {completedSets}/{totalSets}
            </span>
          )}
          {editable && (
            <>
              <CopyComponent
                classNames="p-0!"
                exercises={exercise.exercises || exercise}
                athlete={athlete}
              />
              <img
                src="/img/new-icons/drag-outline.svg"
                className="grabber cursor-grab"
                title={t("athlete-portal:exerciseEditor.dragMoveExercise")}
                alt={t("athlete-portal:exerciseEditor.dragMoveExercise")}
              />
            </>
          )}
          <MenuOptionsEditing
            exercise={exercise}
            dayString={dayString}
            athleteId={athleteId}
            shown={!editDayMode && editable}
          />
        </div>
      </div>
      <ExerciseContent
        exercise={exercise}
        // Restricts PR matching to records actually set on this day: the
        // calendar loads dayPersonalRecords for the whole visible range.
        exerciseDate={date.format(DATE_DAY_FORMAT)}
        someHasRPE={someHasRPE}
        finalSets={finalSets}
        complexTwo={complexTwo}
        complexThree={complexThree}
        complexTwoSets={complexTwoSets}
        complexThreeSets={complexThreeSets}
        editDayMode={editDayMode}
        editable={editable}
        handleInput={handleInput}
        addTempSet={addTempSet}
        removeSet={removeSet}
        removeTempSet={removeTempSet}
        repMaximums={repMaximums}
        withWeight={withWeight}
        fromMetric={fromMetric}
        intensityMeasure={intensityMeasure}
        isRiR={isRiR}
        units={activeUser.units}
      />
      <WorkoutParamsRow
        exercise={exercise}
        newExercise={newExercise}
        editDayMode={editDayMode}
        editable={editable}
        handleInput={handleInput}
        exerciseId={exerciseId}
      />
      {(normalizedNotes.length > 0 || addingNote) && (
        <Notes
          {...{
            exerciseNotes: normalizedNotes,
            exerciseId,
            editDayMode,
            editable,
            handleInput,
          }}
        />
      )}
      <div role="supersets-notes-buttons">
        {!isComplex && !nextIsComplex && !isMetcon && !nextIsMetcon && (
          <SuperSetButton
            {...{
              editDayMode,
              editable,
              isLast,
              isSuperset,
              handleSuperset,
              exercise,
              exercises,
              keyId,
            }}
          />
        )}
        {normalizedNotes.length <= 0 && editDayMode && !addingNote && (
          <button
            data-button-type="text"
            onClick={() => {
              onAddNote();
            }}
            type="button"
          >
            <SvgIcon src="/img/new-icons/note-gradient.svg" alt="add notes" />
            <span>Add Note</span>
          </button>
        )}
        {normalizedNotes.length > 0 && editDayMode && (
          <button
            data-button-type="text-white"
            data-action="cancel"
            onClick={() => {
              setAddingNote(false);
              handleInput({
                value: "",
                field: "notes",
                exerciseId,
              });
            }}
            type="button"
          >
            <img
              src={"/img/new-icons/red-trash.svg"}
              width={16}
              height={16}
              alt="remove note"
            />
            <span>Remove Note</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExerciseEditor;
